import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { useDispatch, useSelector, useStore } from "react-redux";
import { useTypedSelector } from "../../hooks.tsx";
import { useTypingEffect } from "../../hooks/useTypingEffect.ts";
import {
  getAuthenticatedKeyPressHandler,
  getUnauthenticatedKeyPressHandler,
} from "./handleKeyPress.tsx";
import { i18n } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { RootState } from "../../store/types.tsx";
import { SearchBarCompact } from "./SearchBarCompact.tsx";
import { searchFieldActions } from "../../store/search/searchField.tsx";
import { getCircleExamples } from "./examples";
import { getIsConversationWorking } from "../../store/conversation.tsx";
import { stopStream } from "./streamAsk.tsx";

interface SearchFieldProps {
  children?: React.ReactNode;
  onPulseAnimation?: () => void;
  stopPulseAnimation?: () => void;
  isCircleView?: boolean;
}

const SearchField: React.FC<SearchFieldProps> = ({
  onPulseAnimation = () => {},
  stopPulseAnimation = () => {},
  isCircleView = false,
}) => {
  const dispatch = useDispatch();
  const store = useStore<RootState>();
  const { globalSearchText, shouldSubmit, submitImmediately } =
    useTypedSelector((state) => state.searchFieldState);
  const isAuthenticated = useTypedSelector(
    (state) => state.authState.isLoggedIn,
  );

  const selectedConversation = useTypedSelector(
    (state) => state.conversationState.selectedConversation,
  );
  const isSelectedConversationWorking = useTypedSelector((state) =>
    getIsConversationWorking(state, selectedConversation),
  );
  const anonymousSession = useTypedSelector(
    (state) => state.authState.anonymousSession,
  );

  const selectedModel = useSelector(
    (state: RootState) => state.conversationState.selectedModel,
  );

  const [searchText, setSearchText] = useState<string>("");
  const [searchInputError, setSearchInputError] = useState<boolean>(false);
  const [searchInputErrorMessage, setSearchInputErrorMessage] = useState<
    string | undefined
  >(undefined);
  const [animateContent, setAnimateContent] = useState<string>("");
  const [shouldAnimate, setShouldAnimate] = useState<boolean>(false);

  // Circle view placeholder examples (imported and cycled with typewriter effect)
  const [placeholderIndex, setPlaceholderIndex] = useState<number>(0);
  const [placeholderContent, setPlaceholderContent] = useState<string>("");

  useEffect(() => {
    // When not animating, reflect global text
    if (!shouldAnimate) {
      setSearchText(globalSearchText);
    }
  }, [globalSearchText, shouldAnimate]);

  useEffect(() => {
    if (!isCircleView) return;
    const examples = getCircleExamples();
    setPlaceholderIndex(0);
    setPlaceholderContent(examples[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCircleView]);

  const stopAnimation = () => {
    if (stopPulseAnimation) stopPulseAnimation();
  };

  const handleSubmit = React.useCallback(
    (textToSubmit?: string) => {
      // Create a fake keyboard event to reuse existing logic
      const fakeEvent = {
        key: "Enter",
        shiftKey: false,
        preventDefault: () => {},
      } as React.KeyboardEvent<HTMLDivElement>;

      const text = textToSubmit ?? searchText;

      if (isAuthenticated) {
        getAuthenticatedKeyPressHandler({
          searchText: text,
          setSearchText,
          setSearchInputError,
          setSearchInputErrorMessage,
          onPulseAnimation,
          stopAnimation,
          dispatch,
          getState: store.getState,
          selectedConversation,
          selectedModel,
        })(fakeEvent);
      } else {
        getUnauthenticatedKeyPressHandler({
          searchText: text,
          setSearchText,
          setSearchInputError,
          setSearchInputErrorMessage,
          onPulseAnimation,
          stopAnimation,
          currentConversation: selectedConversation,
          dispatch,
          getState: store.getState,
          anonymousSession,
        })(fakeEvent);
      }
    },
    [
      isAuthenticated,
      searchText,
      selectedConversation,
      anonymousSession,
      dispatch,
      store,
      onPulseAnimation,
      stopAnimation,
      selectedModel,
    ],
  );

  const handleChange = (value: string) => {
    setSearchText(value);
    if (value.trim() !== "") {
      setSearchInputError(false);
      setSearchInputErrorMessage(undefined);
    }
  };

  const placeholder = i18n._(
    msg({
      message: "Write here ...",
      context: "Placeholder text for search input.",
    }),
  );

  // Start animation when example triggers submit
  useEffect(() => {
    if (!shouldSubmit) return;
    const fullText =
      typeof globalSearchText === "string" ? globalSearchText : "";
    dispatch(searchFieldActions.clearSubmitFlag());

    if (fullText.trim() === "") {
      handleSubmit("");
      return;
    }

    // Voice mode submits without the typewriter animation: at ~35ms/character
    // it adds seconds of latency to a turn nobody is watching the input for.
    if (submitImmediately) {
      setSearchInputError(false);
      setSearchText("");
      handleSubmit(fullText);
      return;
    }

    setSearchInputError(false);
    setSearchText("");
    setAnimateContent(fullText);
    setShouldAnimate(true);
  }, [
    shouldSubmit,
    submitImmediately,
    globalSearchText,
    handleSubmit,
    dispatch,
  ]);

  // Shared typing effect for animating input - use when user clicks on an example
  const { displayed, typing } = useTypingEffect(animateContent, shouldAnimate, {
    fixedDelayMs: 35,
    onComplete: () => {
      // Add a small natural pause before submitting
      window.setTimeout(() => {
        handleSubmit(animateContent);
        // Now that we've submitted, we can end the animate state
        setShouldAnimate(false);
      }, 160);
    },
  });

  // Drive the input value from typing effect while animating
  useEffect(() => {
    if (shouldAnimate) {
      setSearchText(displayed);
    }
  }, [displayed, shouldAnimate]);

  // Typewriter for circle placeholder (always runs when circle view is active)
  const { displayed: placeholderDisplayed } = useTypingEffect(
    placeholderContent,
    isCircleView,
    {
      fixedDelayMs: 28,
      leadingPauseMs: 200,
      onComplete: () => {
        // After a brief pause, advance to the next example
        window.setTimeout(() => {
          const examples = getCircleExamples();
          const next = (placeholderIndex + 1) % examples.length;
          setPlaceholderIndex(next);
          setPlaceholderContent(examples[next]);
        }, 1200);
      },
    },
  );

  const handleStop = React.useCallback(() => {
    if (!selectedConversation) return;
    try {
      stopStream(selectedConversation);
    } catch (error) {
      console.error("Failed to stop streaming:", error);
    }
  }, [selectedConversation]);

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        width: "100%",
      }}
    >
      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          "& > div": {
            ...(searchInputError && {
              boxShadow: "0 0 0 2px red",
              borderRadius: "18px", // match the SearchBarCompact's border radius
            }),
          },
        }}
      >
        <SearchBarCompact
          value={searchText}
          onChange={handleChange}
          onSubmit={handleSubmit}
          inlineSend={!isAuthenticated}
          placeholder={isCircleView ? placeholderDisplayed : placeholder}
          disabled={isSelectedConversationWorking || typing}
          errorMessage={searchInputErrorMessage}
          isCircleView={isCircleView}
          onStop={handleStop}
        />
      </Box>
    </Box>
  );
};

export default SearchField;
